import styled from "styled-components";
import React, {ReactElement} from "react";
import {Box, Grid, GridProps, IconButton, Tooltip, Typography} from "@mui/material";
import {Colors} from "../Colors";
import {EMPTY_UI} from "../../../variables";
import {TitleBarButton} from "./ContentTitle";
import {TFunction, useTranslation} from "react-i18next";
import {usePermission} from "../../../hooks/usePermission";
import {PermissionLevel} from "../../../app/api/model/user";

const ContentSectionStyles = `margin-bottom: 36px !important;
    border: 1px ${Colors.GRAY_10} solid;
    padding: 14px;
    position: relative;`;

const Section = styled(Box)`
    ${ContentSectionStyles}
`;

const GridSection = styled(Grid)`
    ${ContentSectionStyles}
`;

const SectionTitle = styled(Typography)`
    position: absolute;
    top: -14px;
    left: 15px;
    padding: 0 14px;
    margin: 0 !important;
    background: ${Colors.CONTENT_BACKGROUND};
`;

interface LabeledSectionProps extends GridProps {
    title: string;
    isGridItem?: boolean;
    children?: ReactElement | ReactElement[];
    buttons?: TitleBarButton[];
    dataTest?: string;
}

const renderButtons = ({
    hasPermission,
    t,
    buttons,
}: {
    hasPermission: (permission: PermissionLevel) => boolean;
    t: TFunction;
    buttons: TitleBarButton[];
}) =>
    buttons
        .filter((button) => hasPermission(button.minRequiredPermissionToSee))
        .map((button) => (
            <Tooltip title={`${t(button.tooltip)}`} key={`button.tooltip${button.key}`}>
                <span>
                    <IconButton disabled={button.disabled} onClick={() => button.callback && button.callback()}>
                        {button.icon}
                    </IconButton>
                </span>
            </Tooltip>
        ));

const LabeledSection = ({
    title = EMPTY_UI,
    isGridItem = true,
    buttons = [],
    children,
    dataTest = "labeledSection",
    ...rest
}: LabeledSectionProps) => {
    const {t} = useTranslation();
    const {hasPermission} = usePermission();
    const getContent = () => (
        <>
            <SectionTitle variant={"h5"}>
                {title}
                {renderButtons({hasPermission, t, buttons})}
            </SectionTitle>
            <Grid container>{children}</Grid>
        </>
    );

    return isGridItem ? (
        <GridSection item {...rest} data-test={dataTest}>
            {getContent()}
        </GridSection>
    ) : (
        <Section {...rest} data-test={dataTest}>
            {getContent()}
        </Section>
    );
};

export default LabeledSection;
